import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiJson } from '../api/client.js';
import { FormInput, FormSelect, SearchForm } from '../components/FormControls.jsx';
import { formatDateTime } from '../utils/formatDate.js';
import { ROLE_LABELS } from '../utils/roles.js';

function initials(name) {
  if (!name || typeof name !== 'string') {
    return '?';
  }
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
}

function relativeWhen(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }
  const diffMs = Date.now() - date.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateTime(value);
}

function categoryClass(category) {
  const map = {
    clients: 'audit-cat-clients',
    projects: 'audit-cat-projects',
    tasks: 'audit-cat-tasks',
    team: 'audit-cat-team',
    integrations: 'audit-cat-integrations',
    organization: 'audit-cat-org',
    billing: 'audit-cat-billing',
    security: 'audit-cat-security',
    documents: 'audit-cat-docs',
    assets: 'audit-cat-assets',
  };
  return map[category] || 'audit-cat-other';
}

function metadataLines(meta) {
  if (!meta || typeof meta !== 'object') {
    return [];
  }
  const skip = new Set(['method', 'path', 'input', 'changes']);
  const lines = [];
  Object.entries(meta).forEach(([key, value]) => {
    if (skip.has(key) || value == null || value === '') return;
    if (typeof value === 'object') {
      lines.push({ key, value: JSON.stringify(value) });
      return;
    }
    lines.push({ key: key.replace(/_/g, ' '), value: String(value) });
  });
  if (Array.isArray(meta.changes) && meta.changes.length) {
    lines.push({ key: 'changed fields', value: meta.changes.join(', ') });
  }
  return lines.slice(0, 8);
}

const emptyFilters = { search: '', userId: '', category: '', dateFrom: '', dateTo: '' };

export function AdminAuditLogPage() {
  const [logs, setLogs] = useState({ data: [], meta: {}, filters: { users: [], categories: [] } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    params.set('per_page', '25');
    params.set('page', String(page));
    if (applied.search.trim()) params.set('search', applied.search.trim());
    if (applied.userId) params.set('user_id', applied.userId);
    if (applied.category) params.set('category', applied.category);
    if (applied.dateFrom) params.set('date_from', applied.dateFrom);
    if (applied.dateTo) params.set('date_to', applied.dateTo);

    try {
      const data = await apiJson(`/api/admin/audit-logs?${params}`);
      setLogs(data);
    } catch (err) {
      setError(err.message || 'Failed to load activity log');
      setLogs({ data: [], meta: {}, filters: { users: [], categories: [] } });
    } finally {
      setLoading(false);
    }
  }, [page, applied]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => (Array.isArray(logs.data) ? logs.data : []), [logs.data]);
  const users = logs.filters?.users || [];
  const categories = logs.filters?.categories || [];
  const currentPage = logs.meta?.current_page || page;
  const lastPage = logs.meta?.last_page || 1;
  const total = logs.meta?.total ?? rows.length;

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    setApplied({ ...draft });
  };

  const clearFilters = () => {
    setDraft(emptyFilters);
    setApplied(emptyFilters);
    setPage(1);
  };

  const hasFilters = Boolean(
    applied.search || applied.userId || applied.category || applied.dateFrom || applied.dateTo,
  );

  return (
    <div className="audit-page" data-tour="audit-list">
      <div className="audit-page-header">
        <div>
          <h2 style={{ marginTop: 0, marginBottom: '0.35rem' }} data-tour="page-title">
            Activity log
          </h2>
          <p className="muted" style={{ margin: 0, maxWidth: '40rem' }}>
            Who changed what in your agency — clients, projects, team, integrations, and more.
          </p>
        </div>
        <div className="audit-stat" title="Total matching entries">
          <span className="audit-stat-value">{loading ? '…' : total}</span>
          <span className="audit-stat-label">events</span>
        </div>
      </div>

      <SearchForm className="audit-filters" onSubmit={applyFilters}>
        <FormInput
          placeholder="Search user, action, IP…"
          value={draft.search}
          onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
          aria-label="Search activity"
        />
        <FormSelect
          value={draft.userId}
          onChange={(e) => setDraft((d) => ({ ...d, userId: e.target.value }))}
          aria-label="Filter by user"
        >
          <option value="">All users</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </FormSelect>
        <FormSelect
          value={draft.category}
          onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </FormSelect>
        <FormInput
          type="date"
          value={draft.dateFrom}
          onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
          aria-label="From date"
        />
        <FormInput
          type="date"
          value={draft.dateTo}
          onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
          aria-label="To date"
        />
        <button type="submit" className="btn-secondary">
          Filter
        </button>
        {hasFilters ? (
          <button type="button" className="btn-link" onClick={clearFilters}>
            Clear
          </button>
        ) : null}
      </SearchForm>

      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p className="muted">Loading activity…</p> : null}

      {!loading && rows.length === 0 ? (
        <div className="audit-empty">
          <p className="audit-empty-title">No activity yet</p>
          <p className="muted">
            {hasFilters
              ? 'Nothing matches these filters. Try clearing them.'
              : 'Actions by your team will show up here — creating clients, updating projects, managing integrations, and more.'}
          </p>
        </div>
      ) : null}

      {!loading && rows.length > 0 ? (
        <div className="audit-feed">
          {rows.map((log) => {
            const isOpen = Boolean(expanded[log.id]);
            const details = metadataLines(log.metadata);
            return (
              <article key={log.id} className="audit-item">
                <div className={`audit-avatar ${categoryClass(log.category)}`} aria-hidden>
                  {initials(log.user?.name)}
                </div>
                <div className="audit-item-body">
                  <div className="audit-item-top">
                    <div className="audit-who">
                      <strong>{log.user?.name || 'Unknown user'}</strong>
                      {log.user?.role ? (
                        <span className="muted small"> · {ROLE_LABELS[log.user.role] || log.user.role}</span>
                      ) : null}
                    </div>
                    <time className="audit-when" dateTime={log.created_at} title={formatDateTime(log.created_at)}>
                      <span className="audit-when-rel">{relativeWhen(log.created_at)}</span>
                      <span className="muted small audit-when-abs">{formatDateTime(log.created_at)}</span>
                    </time>
                  </div>

                  <div className="audit-what">
                    <span className={`audit-badge ${categoryClass(log.category)}`}>
                      {log.category_label || 'Other'}
                    </span>
                    <span className="audit-action">{log.action_label || log.action}</span>
                  </div>

                  {(log.summary || log.subject_label || log.subject_id) && (
                    <p className="audit-summary muted">
                      {log.summary ? <span>{log.summary}</span> : null}
                      {log.summary && (log.subject_label || log.subject_id) ? <span> · </span> : null}
                      {log.subject_label || log.subject_id
                        ? `${log.subject_label || 'Record'}${log.subject_id ? ` #${log.subject_id}` : ''}`
                        : null}
                    </p>
                  )}

                  <div className="audit-meta-row">
                    {log.user?.email ? <span className="muted small">{log.user.email}</span> : null}
                    {log.ip_address ? <span className="muted small">IP {log.ip_address}</span> : null}
                    {details.length > 0 ? (
                      <button
                        type="button"
                        className="btn-link audit-details-toggle"
                        onClick={() => setExpanded((prev) => ({ ...prev, [log.id]: !prev[log.id] }))}
                      >
                        {isOpen ? 'Hide details' : 'Details'}
                      </button>
                    ) : null}
                  </div>

                  {isOpen && details.length > 0 ? (
                    <dl className="audit-details">
                      {details.map((line) => (
                        <div key={line.key} className="audit-detail-row">
                          <dt>{line.key}</dt>
                          <dd>{line.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      {lastPage > 1 ? (
        <div className="audit-pagination row-actions" style={{ marginTop: '1rem', gap: '0.75rem' }}>
          <button
            type="button"
            className="btn-secondary"
            disabled={currentPage <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="muted small">
            Page {currentPage} of {lastPage}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={currentPage >= lastPage || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}
